import React, { ReactNode, useEffect, useRef } from "react";
import Script from "next/script";
import styled from "@emotion/styled";
import { ParlorDetailProps } from "@/types/parlor";
import { useRecoilState } from "recoil";

import { flexCentering, GraySpinner } from "goi_common";
import { isMapLoadState } from "../atoms/parlors/matState";

interface Map {
  children: ReactNode;
}

function openInfoWindow(infowindow: any, map: any, marker: any) {
  infowindow.open(map, marker);
}

function MapLayout({
  parlor,
  height = "500px",
}: {
  parlor: ParlorDetailProps;
  height?: string;
}) {
  const [isLoad, setIsLoad] = useRecoilState(isMapLoadState);
  const mapRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  useEffect(() => {
    if (!isLoad || !mapRef) return;

    const latitude = parlor.latitude;
    const longitude = parlor.longitude;

    const mapOptions = {
      center: new window.naver.maps.LatLng(latitude, longitude),
      zoom: 13,
    };
    const map = new window.naver.maps.Map(mapRef.current, mapOptions);
    const markerOptions = {
      position: new window.naver.maps.LatLng(latitude, longitude),
      map,
    };

    const marker = new window.naver.maps.Marker(markerOptions);

    const infowindow = new window.naver.maps.InfoWindow({
      content: `<div style='width:100%; padding: 6px 12px; border-radius: 5px' class='body3_m'>${parlor.companyname}</div>`,
      borderWidth: 1,
      borderRadius: 5,
      pixelOffset: new window.naver.maps.Point(0, -5),
      anchorSize: 0,
    });
    openInfoWindow(infowindow, map, marker);

    return () => map.destroy();
  }, [isLoad, parlor]);

  return (
    <>
      <Script
        src={`https://openapi.map.naver.com/openapi/v3/maps.js?ncpClientId=${process.env.NEXT_PUBLIC_NAVERMAP_KEY}`}
        onLoad={() => setIsLoad(true)}
      />
      <MapLayoutRoot id="mapLayout" height={height}>
        <Map ref={mapRef} height={height}>
          {!isLoad && <GraySpinner />}
        </Map>
      </MapLayoutRoot>
    </>
  );
}

const MapLayoutRoot = styled.div<{ height: string }>`
  position: relative;
  height: ${(props) => props.height};
  width: 100%;
`;

const Map = styled.div<{ height: string }>`
  ${flexCentering("row")}
  height: ${(props) => props.height};
  width: 100%;
  outline: none;
`;

export default MapLayout;
